import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Phone, MapPin, Leaf, Sparkles, CalendarCheck, Layers, ShieldCheck, ThumbsUp, Home as HomeIcon, Star } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { KineticLines } from "../components/Kinetic";
import { PhotoTag } from "../components/PhotoTag";
import { EditorialMarquee } from "../components/Marquee";
import { BeforeAfterSlider } from "../components/BeforeAfterSlider";
import { Seo } from "../components/Seo";
import { BUSINESS, SERVICES, RESULTS, PHOTOS, GOOGLE, REVIEWS } from "../data/site";
import { SUBURBS } from "../data/suburbs";

const CHAPTERS = [
    { num: "01", title: "True Local Care", text: "We're a local Adelaide property care business. When you call, you speak directly with the people who'll do the work." },
    { num: "02", title: "All-In-One Convenience", text: "Lawns, gutters, windows and junk — one business for multiple property-care jobs, booked together in a single request." },
    { num: "03", title: "Straightforward Communication", text: "Tell us what you need, and we'll contact you to confirm the details and arrange a suitable time. No runaround." },
];

const BENEFITS = [
    { icon: MapPin, label: "Local Adelaide business" },
    { icon: Sparkles, label: "Professional workmanship" },
    { icon: ShieldCheck, label: "Reliable service" },
    { icon: ThumbsUp, label: "Quality results" },
    { icon: CalendarCheck, label: "Easy booking" },
    { icon: Layers, label: "Multiple services in one booking" },
    { icon: HomeIcon, label: "One business for multiple property-care jobs" },
];

const FAQS = [
    { q: "How much does lawn mowing cost in Adelaide?", a: "Every property is different, so we confirm every quote free in person before work starts. Quotes are built on a fair hourly labour rate with equipment included, and factors like property size, overgrowth and access affect the final price. For a rough idea in about 30 seconds, try the instant estimate tool on our website." },
    { q: "What areas of Adelaide do you service?", a: "We service all of Adelaide metro, based in the southern suburbs including Flagstaff Hill, Happy Valley, Woodcroft, Aberfoyle Park, Hallett Cove, Morphett Vale, Christies Beach, Seaford and Glenelg." },
    { q: "Can I book more than one service at once?", a: "Yes — you can combine up to three services in a single booking across lawn care, gutter cleaning, window cleaning and junk removal, and we will always work to give you a package price." },
    { q: "How do I get a free quote?", a: "Use the free quote form on our Book page, try the instant estimate tool for a rough price range, or call 0481 831 282. Quotes are free and there is no obligation." },
    { q: "Do you offer regular lawn maintenance?", a: "Yes. Alongside one-off jobs and clean-ups, we look after properties on a regular maintenance schedule, such as monthly lawn and garden care." },
];

export default function Home() {
    const heroRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
    const photoY = useTransform(scrollYProgress, [0, 1], [0, 110]);

    return (
        <main data-testid="home-page" className="pt-16">
            <Seo title="Lawn Mowing Adelaide | 5.0-Star Local Team | Lawn Busters"
                description="Lawn mowing, gutter & window cleaning and junk removal across Adelaide. 5.0-star rated from 28 Google reviews. Free quotes — call 0481 831 282." />
            {/* Hero */}
            <section ref={heroRef} className="relative overflow-hidden">
                <div className="mx-auto grid max-w-6xl items-center gap-12 px-6 pb-20 pt-14 lg:grid-cols-12 lg:pb-28 lg:pt-24">
                    <div className="lg:col-span-7">
                        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
                            className="eyebrow flex items-center gap-2 text-forest-leaf">
                            <Leaf size={12} /> Locally owned &amp; operated in Adelaide
                        </motion.p>
                        <h1 data-testid="hero-headline" className="mt-5 font-display text-4xl font-bold leading-[1.04] tracking-tight text-ink sm:text-5xl lg:text-6xl">
                            <KineticLines lines={["Adelaide's Local", "Property Care", "Experts"]} lineClassName="last:text-forest-leaf" />
                        </h1>
                        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.7 }}
                            className="mt-6 max-w-xl text-base leading-relaxed text-moss">
                            Professional lawn care, gutter &amp; window cleaning, and junk removal to keep your
                            property looking its best.
                        </motion.p>
                        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.7 }}
                            className="mt-9 flex flex-wrap items-center gap-3">
                            <Link to="/book-now" data-testid="hero-book-button"
                                className="rounded-full bg-forest px-7 py-3.5 text-sm font-bold text-bone transition-colors hover:bg-forest-deep">
                                Book Now <ArrowRight size={14} className="ml-1 inline" />
                            </Link>
                            <Link to="/before-after" data-testid="hero-view-work-button"
                                className="rounded-full border border-forest/25 px-7 py-3.5 text-sm font-bold text-forest transition-colors hover:bg-bone-subtle">
                                View Our Work
                            </Link>
                            <Link to="/pricing" data-testid="hero-pricing-button"
                                className="rounded-full border border-forest/25 px-7 py-3.5 text-sm font-bold text-forest transition-colors hover:bg-bone-subtle">
                                Pricing
                            </Link>
                            <Link to="/book-now?type=quote" data-testid="hero-quote-button"
                                className="rounded-full bg-ochre px-7 py-3.5 text-sm font-bold text-forest-night transition-colors hover:bg-clay hover:text-bone">
                                Get a Free Quote
                            </Link>
                            <a href={BUSINESS.phoneHref} data-testid="hero-phone-button"
                                className="flex items-center gap-2 rounded-full bg-ochre/15 px-5 py-3.5 text-sm font-bold text-forest transition-colors hover:bg-ochre/25">
                                <Phone size={14} /> {BUSINESS.phone}
                            </a>
                        </motion.div>
                        <motion.a href={GOOGLE.url} target="_blank" rel="noopener noreferrer" data-testid="hero-google-rating"
                            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.7 }}
                            className="mt-5 inline-flex items-center gap-2.5 rounded-full border border-forest/15 bg-bone-card px-4 py-2 transition-colors hover:border-ochre">
                            <span className="flex gap-0.5 text-ochre">
                                {[...Array(5)].map((_, i) => <Star key={i} size={13} fill="currentColor" />)}
                            </span>
                            <span className="text-xs font-bold text-ink">{GOOGLE.rating}</span>
                            <span className="text-xs font-semibold text-moss">from {GOOGLE.count} Google reviews</span>
                        </motion.a>
                    </div>
                    <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.4, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                        className="lg:col-span-5">
                        <div className="photo-frame relative overflow-hidden rounded-3xl border border-forest/15">
                            <motion.img src={PHOTOS.hero} alt="The Lawn Busters team mowing and edging an Adelaide lawn" style={{ y: photoY }}
                                className="aspect-[4/3] w-full scale-110 object-cover" />
                            <div className="absolute right-4 top-4 rounded-full bg-bone/90 px-4 py-2 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-forest backdrop-blur-sm">
                                Adelaide SA
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            <EditorialMarquee />

            {/* Google reviews — front and centre, right after the hero */}
            <section className="bg-bone-subtle py-20 lg:py-28" data-testid="reviews-section">
                <div className="mx-auto max-w-6xl px-6">
                    <Reveal>
                        <p className="eyebrow text-forest-leaf">Google reviews</p>
                        <div className="mt-3 flex flex-wrap items-end gap-x-6 gap-y-3">
                            <h2 className="font-display text-2xl font-bold tracking-tight text-ink sm:text-3xl lg:text-4xl">
                                Rated {GOOGLE.rating} by your Adelaide neighbours
                            </h2>
                            <div data-testid="google-rating-badge" className="flex items-center gap-2.5 pb-1.5">
                                <span className="flex gap-0.5 text-ochre">
                                    {[...Array(5)].map((_, i) => <Star key={i} size={17} fill="currentColor" />)}
                                </span>
                                <span className="text-sm font-semibold text-moss">{GOOGLE.rating} from {GOOGLE.count} Google reviews</span>
                            </div>
                        </div>
                    </Reveal>
                    <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {REVIEWS.map((r, i) => (
                            <Reveal key={r.name} delay={i * 0.1}>
                                <figure data-testid={r.testid} className="flex h-full flex-col rounded-3xl border border-forest/10 bg-bone-card p-8">
                                    <span className="flex gap-0.5 text-ochre">
                                        {[...Array(5)].map((_, s) => <Star key={s} size={15} fill="currentColor" />)}
                                    </span>
                                    <blockquote className="mt-5 flex-1 font-display text-lg leading-relaxed tracking-tight text-ink">
                                        &ldquo;{r.text}&rdquo;
                                    </blockquote>
                                    <figcaption className="mt-7 flex items-center gap-3 border-t border-forest/10 pt-5">
                                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-forest/10 font-display text-sm font-bold text-forest">
                                            {r.name.split(" ").map((w) => w[0]).join("")}
                                        </span>
                                        <div>
                                            <p className="text-sm font-bold text-ink">{r.name}</p>
                                            <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.18em] text-moss">Google review</p>
                                        </div>
                                    </figcaption>
                                </figure>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.15}>
                        <div className="mt-10 flex flex-wrap gap-3">
                            <a href={GOOGLE.url} target="_blank" rel="noopener noreferrer" data-testid="read-reviews-button"
                                className="rounded-full bg-forest px-7 py-3.5 text-sm font-bold text-bone transition-colors hover:bg-forest-deep">
                                Read All Reviews on Google <ArrowRight size={14} className="ml-1 inline" />
                            </a>
                            <a href={GOOGLE.url} target="_blank" rel="noopener noreferrer" data-testid="leave-review-button"
                                className="rounded-full border border-forest/25 px-7 py-3.5 text-sm font-bold text-forest transition-colors hover:bg-bone">
                                Leave Us a Review
                            </a>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* Numbered manifesto chapters */}
            <section className="mx-auto max-w-6xl px-6 py-20 lg:py-28">
                <Reveal>
                    <p className="eyebrow text-forest-leaf">The Lawn Busters way</p>
                </Reveal>
                <div className="mt-10 divide-y divide-forest/10 border-y border-forest/10">
                    {CHAPTERS.map((c, i) => (
                        <Reveal key={c.num} delay={i * 0.08}>
                            <div data-testid={`manifesto-chapter-${c.num}`} className="grid gap-4 py-10 lg:grid-cols-12 lg:items-baseline">
                                <span className="font-mono text-sm font-semibold text-ochre lg:col-span-2">{c.num}</span>
                                <h2 className="font-display text-2xl font-bold tracking-tight text-ink sm:text-3xl lg:col-span-4">{c.title}</h2>
                                <p className="text-base leading-relaxed text-moss lg:col-span-6">{c.text}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* Services */}
            <section className="bg-bone-subtle py-20 lg:py-28" data-testid="services-section">
                <div className="mx-auto max-w-6xl px-6">
                    <Reveal>
                        <p className="eyebrow text-forest-leaf">Our services</p>
                        <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-ink sm:text-3xl lg:text-4xl">
                            One team for your whole property
                        </h2>
                    </Reveal>
                    <div className="mt-12 grid gap-6 md:grid-cols-3">
                        {SERVICES.map((s, i) => (
                            <Reveal key={s.slug} delay={i * 0.1}>
                                <div data-testid={s.testid}
                                    className="group flex h-full flex-col overflow-hidden rounded-3xl border border-forest/10 bg-bone-card transition-transform duration-300 hover:-translate-y-1.5">
                                    <div className="relative overflow-hidden">
                                        <img src={s.img} alt={s.name} className="aspect-[16/10] w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                                        {!s.real && <PhotoTag />}
                                    </div>
                                    <div className="flex flex-1 flex-col p-6">
                                        <h3 className="font-display text-xl font-semibold tracking-tight text-ink">{s.name}</h3>
                                        <p className="mt-2 flex-1 text-sm leading-relaxed text-moss">{s.short}</p>
                                        <Link to={s.path} data-testid={`${s.testid}-learn-more`}
                                            className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-forest transition-colors hover:text-forest-leaf">
                                            Learn More <ArrowRight size={14} />
                                        </Link>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Why choose us */}
            <section className="mx-auto max-w-6xl px-6 py-20 lg:py-28" data-testid="why-choose-section">
                <Reveal>
                    <p className="eyebrow text-forest-leaf">Why choose us</p>
                    <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-ink sm:text-3xl lg:text-4xl">
                        Your local Adelaide property care team
                    </h2>
                </Reveal>
                <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {BENEFITS.map((b, i) => (
                        <Reveal key={b.label} delay={i * 0.05} className={i === 0 ? "sm:col-span-2 lg:col-span-1" : ""}>
                            <div data-testid={`benefit-${i}`} className="flex h-full items-center gap-4 rounded-2xl border border-forest/10 bg-bone-card p-6">
                                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-forest/10 text-forest">
                                    <b.icon size={18} />
                                </span>
                                <p className="text-sm font-semibold text-ink">{b.label}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* Before & After spotlight */}
            <section className="bg-forest-night py-20 text-bone lg:py-28" data-testid="before-after-section">
                <div className="mx-auto max-w-6xl px-6">
                    <Reveal>
                        <p className="eyebrow text-ochre">Before &amp; After</p>
                        <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">See The Difference</h2>
                        <p className="mt-4 max-w-xl text-base text-bone/70">Real before-and-after results from our Adelaide jobs.</p>
                    </Reveal>
                    <div className="mt-12 grid gap-10 lg:grid-cols-2">
                        {RESULTS.map((r, i) => (
                            <Reveal key={r.id} delay={i * 0.1}>
                                <BeforeAfterSlider dark before={r.before} after={r.after} title={r.title} blurb={r.blurb} testid={`home-${r.id}-slider`} />
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.15}>
                        <Link to="/before-after" data-testid="view-more-results-button"
                            className="mt-6 inline-block rounded-full bg-ochre px-7 py-3.5 text-sm font-bold text-forest-night transition-colors hover:bg-bone">
                            View More Results <ArrowRight size={14} className="ml-1 inline" />
                        </Link>
                    </Reveal>
                </div>
            </section>

            {/* Multiple services */}
            <section className="mx-auto max-w-6xl px-6 py-20 lg:py-28" data-testid="multiple-services-section">
                <div className="grid items-center gap-10 rounded-3xl border border-forest/10 bg-bone-card p-8 lg:grid-cols-12 lg:p-14">
                    <Reveal className="lg:col-span-7">
                        <p className="eyebrow text-forest-leaf">Save time</p>
                        <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-ink sm:text-3xl">Need more than one job done?</h2>
                        <p className="mt-4 max-w-lg text-base leading-relaxed text-moss">
                            Save time by booking multiple services together. Choose any 2 or 3 services from our
                            booking form and tell us what you need — one form, one visit, sorted.
                        </p>
                        <Link to="/book-now" data-testid="book-multiple-services-button"
                            className="mt-8 inline-block rounded-full bg-forest px-7 py-3.5 text-sm font-bold text-bone transition-colors hover:bg-forest-deep">
                            Book Multiple Services
                        </Link>
                    </Reveal>
                    <Reveal className="lg:col-span-5" delay={0.1}>
                        <div className="space-y-3">
                            {["Lawn Care + Gutter Cleaning", "Gutter Cleaning + Window Cleaning", "Lawn Care + Gutter Cleaning + Junk Removal"].map((combo) => (
                                <div key={combo} className="flex items-center gap-3 rounded-2xl border border-forest/10 bg-bone-subtle px-5 py-4">
                                    <Layers size={16} className="shrink-0 text-forest-leaf" />
                                    <p className="text-sm font-semibold text-ink">{combo}</p>
                                </div>
                            ))}
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* Pricing teaser */}
            <section className="bg-forest py-20 text-bone lg:py-24" data-testid="pricing-teaser-section">
                <div className="mx-auto max-w-6xl px-6">
                    <Reveal>
                        <p className="eyebrow text-ochre">Pricing</p>
                        <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
                            Fair, upfront pricing — no surprises
                        </h2>
                        <p className="mt-4 max-w-xl text-base leading-relaxed text-bone/70">
                            Every job is different, so we quote based on size, condition, access and time. Here's
                            how every quote works.
                        </p>
                    </Reveal>
                    <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        {[
                            { figure: "Fair hourly rate", label: "Labour & equipment included" },
                            { figure: "Per gutter side", label: "Gutter cleaning priced by side" },
                            { figure: "Per trailer load", label: "Tipping & disposal" },
                            { figure: "Deposit on big jobs", label: "Secures your booking day" },
                        ].map((p, i) => (
                            <Reveal key={p.label} delay={i * 0.06}>
                                <div data-testid={`pricing-teaser-${i}`} className="h-full rounded-2xl border border-bone/15 bg-forest-deep p-6">
                                    <p className="font-display text-2xl font-bold tracking-tight text-ochre">{p.figure}</p>
                                    <p className="mt-2 text-sm text-bone/70">{p.label}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.15}>
                        <div className="mt-9 flex flex-wrap gap-3">
                            <Link to="/pricing" data-testid="pricing-teaser-button"
                                className="inline-block rounded-full bg-ochre px-8 py-3.5 text-sm font-bold text-forest-night transition-colors hover:bg-bone">
                                How We Price Our Quotes <ArrowRight size={14} className="ml-1 inline" />
                            </Link>
                            <Link to="/estimate" data-testid="pricing-teaser-estimate-button"
                                className="inline-block rounded-full border border-bone/30 px-8 py-3.5 text-sm font-bold text-bone transition-colors hover:bg-bone/10">
                                Get an Instant Estimate
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* Local Adelaide */}
            <section className="bg-bone-subtle py-20 lg:py-28" data-testid="local-adelaide-section">
                <div className="mx-auto grid max-w-6xl items-center gap-12 px-6 lg:grid-cols-12">
                    <Reveal className="lg:col-span-6">
                        <div className="photo-frame relative overflow-hidden rounded-3xl border border-forest/15">
                            <img src={PHOTOS.teamGroup} alt="The Lawn Busters team" className="aspect-[4/3] w-full object-cover" />
                        </div>
                    </Reveal>
                    <div className="lg:col-span-6">
                        <Reveal>
                            <p className="eyebrow text-forest-leaf">Areas we service</p>
                            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-ink sm:text-3xl lg:text-4xl">Proudly Local to Adelaide</h2>
                            <p className="mt-5 max-w-lg text-base leading-relaxed text-moss">
                                We're a local Adelaide property care business helping homeowners and property
                                owners keep their outdoor spaces clean, tidy and well maintained.
                            </p>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <div data-testid="areas-we-service" className="mt-8 flex flex-wrap gap-3">
                                {SUBURBS.map((s) => (
                                    <Link key={s.slug} to={`/areas/${s.slug}`} data-testid={`home-area-chip-${s.slug}`}
                                        className="rounded-full border border-forest/20 bg-bone px-4 py-2 text-xs font-semibold text-forest transition-colors hover:border-ochre hover:bg-ochre/10">
                                        {s.name}
                                    </Link>
                                ))}
                                <Link to="/areas" data-testid="home-area-chip-all"
                                    className="rounded-full bg-forest px-4 py-2 text-xs font-bold text-bone transition-colors hover:bg-forest-deep">
                                    All Adelaide Metro
                                </Link>
                            </div>
                            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-moss">
                                All of Adelaide — southern suburbs our home base
                            </p>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* FAQ — text matches the FAQPage structured data in index.html verbatim */}
            <section className="bg-bone-subtle py-20 lg:py-28" data-testid="faq-section">
                <div className="mx-auto max-w-4xl px-6">
                    <Reveal>
                        <p className="eyebrow text-forest-leaf">Common questions</p>
                        <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-ink sm:text-3xl lg:text-4xl">
                            Everything Adelaide asks us
                        </h2>
                    </Reveal>
                    <div className="mt-12 space-y-5">
                        {FAQS.map((f, i) => (
                            <Reveal key={f.q} delay={i * 0.06}>
                                <div data-testid={`faq-item-${i}`} className="rounded-3xl border border-forest/10 bg-bone-card p-7">
                                    <h3 className="font-display text-lg font-bold tracking-tight text-ink">{f.q}</h3>
                                    <p className="mt-3 text-sm leading-relaxed text-moss">{f.a}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="bg-forest py-20 text-center text-bone lg:py-28" data-testid="final-cta-section">
                <Reveal>
                    <h2 className="mx-auto max-w-2xl font-display text-2xl font-bold leading-tight tracking-tight sm:text-3xl lg:text-4xl">
                        Ready to get your property looking its best?
                    </h2>
                    <p className="mx-auto mt-5 max-w-xl text-base text-bone/70">
                        Whether you need lawn care, gutter or window cleaning, junk removal, or a combination of
                        services, we're ready to help.
                    </p>
                    <Link to="/book-now" data-testid="final-cta-book-button"
                        className="mt-9 inline-block rounded-full bg-ochre px-9 py-4 text-sm font-bold text-forest-night transition-colors hover:bg-bone">
                        Book Your Service <ArrowRight size={14} className="ml-1 inline" />
                    </Link>
                </Reveal>
            </section>
        </main>
    );
}
